import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calculator, Users, Target, Award, Shield, Heart, Lightbulb } from "lucide-react";
import { useNavigate } from "react-router-dom";

const AboutUs = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md shadow-sm border-b sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                <Calculator className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-slate-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                Tax Helper Pros
              </span>
            </div>
            
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="text-slate-600 hover:text-slate-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Users className="w-12 h-12 text-blue-600" />
            <h1 className="text-4xl font-bold text-slate-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              About Tax Helper Pros
            </h1>
          </div>
          <p className="text-lg text-slate-600">
            Revolutionizing tax preparation with intelligent automation and pay-per-use pricing
          </p>
        </div>

        {/* Mission Statement */}
        <Card className="shadow-xl border-0 mb-8">
          <CardHeader className="bg-blue-50 border-b">
            <CardTitle className="flex items-center gap-3 text-blue-800">
              <Target className="w-6 h-6" />
              Our Mission
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 bg-blue-50">
            <div className="space-y-4 text-blue-800">
              <p className="text-lg font-medium">
                To democratize professional tax preparation by making advanced financial analysis tools accessible, affordable, and incredibly simple to use.
              </p>
              <p>
                We believe everyone deserves access to professional-grade tax preparation tools without the traditional barriers of expensive subscriptions or complex software.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-0">
          <CardContent className="p-8">
            <div className="prose prose-slate max-w-none" style={{fontFamily: 'Inter, sans-serif'}}>
              
              <h2 className="text-2xl font-bold text-slate-800 mt-0 mb-4">Our Story</h2>
              <p className="text-slate-700 mb-6">
                Tax Helper Pros was born from a simple frustration: why should individuals and small businesses pay hefty monthly subscriptions for tax software they only use during tax season? Our founders, experienced financial technology professionals, recognized that the traditional software model was broken.
              </p>
              
              <p className="text-slate-700 mb-6">
                In 2024, we launched with a revolutionary pay-per-use model that charges users only for what they actually process. No monthly fees, no annual subscriptions, no hidden costs – just transparent, fair pricing that scales with your needs.
              </p>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">What Makes Us Different</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    Zero Data Storage
                  </h4>
                  <p className="text-sm text-green-700">
                    Your sensitive financial documents are processed and immediately deleted. We never store your personal tax information.
                  </p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                    <Calculator className="w-5 h-5" />
                    AI-Powered Analysis
                  </h4>
                  <p className="text-sm text-blue-700">
                    Advanced machine learning algorithms automatically categorize expenses and identify potential deductions.
                  </p>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
                    <Heart className="w-5 h-5" />
                    Pay-Per-Use Pricing
                  </h4>
                  <p className="text-sm text-purple-700">
                    Only pay for what you process. $1 per PDF upload + $1 per page. No subscriptions, ever.
                  </p>
                </div>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h4 className="font-semibold text-orange-800 mb-2 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5" />
                    Professional Quality
                  </h4>
                  <p className="text-sm text-orange-700">
                    Bank-level security and compliance with professional-grade output suitable for tax professionals.
                  </p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">Our Technology</h2>
              <p className="text-slate-700 mb-4">
                Tax Helper Pros leverages cutting-edge artificial intelligence and machine learning to analyze financial documents with unprecedented accuracy. Our platform:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-6">
                <li><strong>Optical Character Recognition (OCR):</strong> Extracts text and numerical data from bank statements with 99.9% accuracy</li>
                <li><strong>Natural Language Processing:</strong> Understands transaction descriptions to automatically categorize expenses</li>
                <li><strong>Pattern Recognition:</strong> Identifies recurring transactions and business patterns</li>
                <li><strong>Tax Code Integration:</strong> Stays updated with current IRS guidelines and Schedule C requirements</li>
                <li><strong>Cloud Processing:</strong> Scalable infrastructure that processes documents in seconds, not minutes</li>
              </ul>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">Security & Compliance</h2>
              <p className="text-slate-700 mb-4">
                We understand that financial data is among the most sensitive information you can share. That's why we've built Tax Helper Pros with security-first principles:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-6">
                <li><strong>End-to-End Encryption:</strong> All data is encrypted using AES-256 encryption during transmission and processing</li>
                <li><strong>SOC 2 Type II Compliance:</strong> Independently audited security controls and procedures</li>
                <li><strong>Zero Persistent Storage:</strong> Your documents are processed in secure memory and immediately deleted</li>
                <li><strong>PCI DSS Compliance:</strong> Payment processing through Stripe with the highest security standards</li>
              </ul>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">Our Commitment to You</h2>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mb-6">
                <div className="space-y-4 text-slate-700">
                  <div className="flex items-start gap-3">
                    <Award className="w-6 h-6 text-blue-600 mt-1" />
                    <div>
                      <h4 className="font-semibold text-slate-800 mb-1">Accuracy Guarantee</h4>
                      <p>We stand behind the accuracy of our AI analysis with a satisfaction guarantee.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Shield className="w-6 h-6 text-green-600 mt-1" />
                    <div>
                      <h4 className="font-semibold text-slate-800 mb-1">Privacy Promise</h4>
                      <p>Your data will never be sold, shared, or used for any purpose other than processing your request.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Heart className="w-6 h-6 text-red-600 mt-1" />
                    <div>
                      <h4 className="font-semibold text-slate-800 mb-1">Customer First</h4>
                      <p>Every feature we build is designed to save you time and money during tax season.</p>
                    </div>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">Looking Forward</h2>
              <p className="text-slate-700 mb-4">
                We're constantly innovating to make tax preparation even simpler. Our roadmap includes:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-6">
                <li>Multi-year tax comparison and trend analysis</li>
                <li>Integration with popular accounting software</li>
                <li>Mobile apps for iOS and Android</li>
                <li>Advanced reporting and analytics dashboards</li>
                <li>Tax professional collaboration tools</li>
              </ul>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">Contact Our Team</h2>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-800 mb-2">We'd love to hear from you! Whether you have questions, feedback, or suggestions, reach out to us:</p>
                <div className="text-blue-800">
                  <p className="font-medium">General Inquiries: hello@taxhelperpros.com</p>
                  <p className="font-medium">Support: support@taxhelperpros.com</p>
                  <p className="font-medium">Security: security@taxhelperpros.com</p>
                  <p className="mt-2">Tax Helper Pros LLC</p>
                  <p>United States</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-center gap-4 mt-8">
          <Button 
            onClick={() => navigate('/')}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Back to Home
          </Button>
          <Button 
            variant="outline"
            onClick={() => navigate('/auth')}
            className="border-blue-600 text-blue-600 hover:bg-blue-50"
          >
            Get Started
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;